/* ============================================================
   Quill&Steel — Screenshot carousel
   ------------------------------------------------------------
   Two independent enhancements over markup that already works
   without either: a lightbox on the thumbnails, and arrows and
   dots over a track that is a real scroll container.

   Progressive enhancement throughout. Every thumbnail is an <a>
   to the full image, so if this script never runs, or the
   browser has no <dialog>, clicking still opens the image on
   its own. The track scrolls by touch, trackpad and keyboard
   with nothing bound to it, so a dead script costs the controls
   and nothing else.

   Native <dialog> rather than a hand-rolled overlay: showModal()
   gives the focus trap, the ESC handler, inert background
   content and the top-layer promotion for free, and all four are
   things a bespoke lightbox gets wrong.

   Lifted out of app-waitlist.js on 2026-08-14, unchanged apart
   from the names, so the carousel outlives the waitlist page.
   ============================================================ */
(function () {
  "use strict";

  function initGallery(root) {
    var dialog = root.querySelector("[data-sc-lightbox-dialog]");
    var links = Array.prototype.slice.call(root.querySelectorAll("[data-sc-lightbox]"));

    if (!dialog || !links.length) return;

    // No <dialog> support: leave the links alone so they still open the image.
    if (typeof dialog.showModal !== "function") return;

    // Guard against double-binding when the theme editor reloads a section that
    // was already initialised on page load.
    if (dialog.dataset.scReady === "true") return;
    dialog.dataset.scReady = "true";

    var image = dialog.querySelector("[data-sc-lightbox-image]");
    var caption = dialog.querySelector("[data-sc-lightbox-caption]");
    var prev = dialog.querySelector("[data-sc-lightbox-prev]");
    var next = dialog.querySelector("[data-sc-lightbox-next]");
    var index = 0;

    function show(i) {
      // Wraps at both ends, so the arrows never dead-end on a short gallery.
      index = (i + links.length) % links.length;

      var link = links[index];
      var text = link.getAttribute("data-caption") || "";

      image.src = link.getAttribute("href");
      image.alt = link.getAttribute("data-alt") || "";
      caption.textContent = text;
      caption.hidden = text === "";

      // A single screenshot has nowhere to page to.
      var many = links.length > 1;
      prev.hidden = !many;
      next.hidden = !many;
    }

    links.forEach(function (link, i) {
      link.addEventListener("click", function (event) {
        // Let modified clicks (new tab, save as) behave normally.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        show(i);
        dialog.showModal();
      });
    });

    prev.addEventListener("click", function () {
      show(index - 1);
    });

    next.addEventListener("click", function () {
      show(index + 1);
    });

    dialog.querySelector("[data-sc-lightbox-close]").addEventListener("click", function () {
      dialog.close();
    });

    dialog.addEventListener("keydown", function (event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        show(index - 1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        show(index + 1);
      }
    });

    // Clicking the backdrop closes. The dialog itself is only the padded frame
    // around .screenshot-carousel__lightbox-inner, so a click landing on it is a click
    // outside the picture. ESC is handled by <dialog> natively.
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) dialog.close();
    });

    // Drop the full-size image on close so a 2200px screenshot is not held in
    // memory for the rest of the visit.
    dialog.addEventListener("close", function () {
      image.removeAttribute("src");
    });
  }

  /* ==========================================================
     Carousel
     ----------------------------------------------------------
     The track scrolls itself; nothing here positions anything.
     Arrows and dots set scrollLeft by whole viewports, and the
     scroll handler reads the position back out to light the
     right dot. That keeps the controls honest when the user
     swipes or flicks instead of clicking.
     ========================================================== */
  function initCarousel(root) {
    var carousel = root.querySelector("[data-sc-carousel]");
    if (!carousel || carousel.dataset.scReady === "true") return;

    var track = carousel.querySelector("[data-sc-track]");
    var prev = carousel.querySelector("[data-sc-prev]");
    var next = carousel.querySelector("[data-sc-next]");
    var dots = root.querySelector("[data-sc-dots]");
    if (!track || !prev || !next || !dots) return;

    carousel.dataset.scReady = "true";

    // Paging is by slides, not by viewports. Six screenshots three-across is
    // two pages, and a viewport-based count says three: the track is only
    // 1.02 viewports longer than itself, so the third dot would scroll to a
    // position that does not exist and could never light up.
    //
    // How many fit is measured rather than hardcoded, so the breakpoints stay
    // in CSS and this file never learns the numbers.
    function metrics() {
      var slides = track.children;
      if (!slides.length) return null;

      var slideWidth = slides[0].getBoundingClientRect().width;
      if (!slideWidth) return null;

      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      var step = slideWidth + gap;

      // +gap because the last visible slide in a row has no trailing gutter.
      var perView = Math.max(1, Math.round((track.clientWidth + gap) / step));

      return {
        step: step,
        perView: perView,
        pages: Math.max(1, Math.ceil(slides.length / perView)),
        pageWidth: step * perView,
        maxScroll: track.scrollWidth - track.clientWidth
      };
    }

    function pageCount() {
      var m = metrics();
      return m ? m.pages : 1;
    }

    function currentPage() {
      var m = metrics();
      if (!m) return 0;

      // The final page is usually short, so its scroll position is the clamped
      // maximum rather than a whole multiple of pageWidth. Without this the
      // last dot never lights up.
      if (m.maxScroll - track.scrollLeft <= 2) return m.pages - 1;
      return Math.min(m.pages - 1, Math.round(track.scrollLeft / m.pageWidth));
    }

    function overflows() {
      return track.scrollWidth - track.clientWidth > 2;
    }

    function buildDots() {
      var pages = pageCount();
      dots.textContent = "";

      for (var i = 0; i < pages; i++) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "screenshot-carousel__dot";
        dot.setAttribute("role", "tab");
        dot.setAttribute("aria-label", "Screenshots, page " + (i + 1) + " of " + pages);
        dot.setAttribute("aria-selected", "false");
        dot.dataset.page = String(i);
        dots.appendChild(dot);
      }
    }

    function sync() {
      var showControls = overflows();
      prev.hidden = !showControls;
      next.hidden = !showControls;
      dots.hidden = !showControls;
      if (!showControls) return;

      var page = currentPage();
      var pages = pageCount();

      prev.disabled = page <= 0;
      next.disabled = page >= pages - 1;

      var children = dots.children;
      for (var i = 0; i < children.length; i++) {
        children[i].setAttribute("aria-selected", i === page ? "true" : "false");
      }
    }

    function goTo(page) {
      var m = metrics();
      if (!m) return;

      var target = Math.max(0, Math.min(page, m.pages - 1)) * m.pageWidth;
      track.scrollLeft = Math.min(target, m.maxScroll);
    }

    prev.addEventListener("click", function () {
      goTo(currentPage() - 1);
    });

    next.addEventListener("click", function () {
      goTo(currentPage() + 1);
    });

    dots.addEventListener("click", function (event) {
      var dot = event.target.closest("[data-page]");
      if (dot) goTo(parseInt(dot.dataset.page, 10));
    });

    // Coalesced to one update per frame: scroll fires far faster than the dots
    // need repainting, and each sync reads layout.
    var ticking = false;
    track.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          sync();
        });
      },
      { passive: true }
    );

    // Page count changes with the breakpoint, so the dots are rebuilt on resize.
    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buildDots();
        sync();
      }, 150);
    });

    buildDots();
    sync();

    // Thumbnails load lazily, so scrollWidth right after parse can be short.
    window.addEventListener("load", function () {
      buildDots();
      sync();
    });
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll(".screenshot-carousel");
    Array.prototype.forEach.call(roots, function (root) {
      initGallery(root);
      initCarousel(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // The theme editor swaps a section's markup without reloading the page, which
  // leaves the new thumbnails unbound.
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
